import {
  DocumentTextIcon,
  CogIcon,
  CalendarIcon,
  BookOpenIcon,
  CodeBracketIcon,
  UsersIcon,
  DocumentIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";

// Importing TargetIcon separately as it might be named differently
import { AdjustmentsHorizontalIcon as TargetIcon } from "@heroicons/react/24/outline";
import type { ProjectPhase } from "@/features/Projects";

export const PROJECT_PHASES: ProjectPhase[] = [
  "description",
  "objectives", 
  "technical_solution",
  "milestones",
  "stories",
  "schema",
  "resources",
  "proposal",
  "execution"
];

export const PHASE_CONFIG: Record<
  ProjectPhase, 
  {
    title: string;
    description: string;
    icon: any;
    color: string;
    order: number;
  }
> = {
  description: {
    title: "Descrição",
    description: "Definição detalhada do projeto processada pela IA",
    icon: DocumentTextIcon,
    color: "blue",
    order: 1,
  },
  objectives: {
    title: "Objetivos",
    description: "Objetivos SMART gerados automaticamente",
    icon: TargetIcon,
    color: "green", 
    order: 2,
  },
  technical_solution: {
    title: "Solução Técnica",
    description: "Stack tecnológica e arquitetura sugerida",
    icon: CogIcon,
    color: "purple",
    order: 3,
  },
  milestones: {
    title: "Marcos",
    description: "Timeline e principais entregas do projeto",
    icon: CalendarIcon,
    color: "orange",
    order: 4,
  },
  stories: {
    title: "User Stories",
    description: "Histórias de usuário e critérios de aceitação", 
    icon: BookOpenIcon,
    color: "indigo",
    order: 5,
  },
  schema: {
    title: "Schema Técnico",
    description: "APIs, modelos de dados e documentação",
    icon: CodeBracketIcon,
    color: "cyan",
    order: 6,
  },
  resources: {
    title: "Recursos",
    description: "Equipe necessária e estimativas de custo",
    icon: UsersIcon,
    color: "pink",
    order: 7,
  },
  proposal: {
    title: "Proposta",
    description: "Documento comercial final para o cliente",
    icon: DocumentIcon,
    color: "amber",
    order: 8,
  },
  execution: {
    title: "Execução",
    description: "Acompanhamento do desenvolvimento",
    icon: PlayIcon,
    color: "emerald",
    order: 9,
  },
};

// Função para verificar se uma fase está acessível
export function isPhaseAccessible(
  targetPhase: ProjectPhase, 
  currentPhase: ProjectPhase,
  project?: { [key: string]: any }
): boolean {
  const targetOrder = PHASE_CONFIG[targetPhase].order;
  const currentOrder = PHASE_CONFIG[currentPhase].order;
  
  // Sempre pode acessar a fase atual
  if (targetPhase === currentPhase) {
    return true;
  }
  
  // Pode acessar fases anteriores (simplificado)
  if (targetOrder < currentOrder) {
    return true;
  }
  
  // Pode acessar a próxima fase imediata
  return targetOrder === currentOrder + 1;
}

// Função para verificar se uma fase tem conteúdo (foi completada)
export function isPhaseCompleted(
  targetPhase: ProjectPhase,
  project?: { [key: string]: any }
): boolean {
  if (!project) return false;
  
  switch (targetPhase) {
    case 'initial_description':
      return !!(project.description?.length > 20);
    case 'objectives':
      return !!(project.objectives?.length > 0);
    case 'technical_solution':
      return !!(project.technicalSolution);
    case 'milestones':
      return !!(project.timeline?.milestones?.length > 0);
    case 'stories':
      return !!(project.stories?.epics?.length > 0);
    case 'schema':
      return !!(project.schema?.dataModels?.length > 0 || project.schema?.apiEndpoints?.length > 0);
    case 'resources':
      return !!(project.resources);
    case 'proposal':
      return !!(project.proposal);
    case 'execution':
      return !!(project.executionPlan);
    default:
      return false;
  }
}

// Função para obter status da fase (completed, current, locked, available)
export function getPhaseStatus(
  targetPhase: ProjectPhase,
  currentPhase: ProjectPhase,
  project?: { [key: string]: any }
): 'completed' | 'current' | 'available' | 'locked' {
  if (targetPhase === currentPhase) {
    return 'current';
  }
  
  const targetOrder = PHASE_CONFIG[targetPhase].order;
  const currentOrder = PHASE_CONFIG[currentPhase].order;
  
  if (targetOrder < currentOrder) {
    return 'completed';
  }
  
  if (targetOrder === currentOrder + 1) {
    return 'available';
  }
  
  return 'locked';
}

// Função para obter a próxima fase
export function getNextPhase(currentPhase: ProjectPhase): ProjectPhase | null {
  const currentOrder = PHASE_CONFIG[currentPhase].order;
  const nextPhase = PROJECT_PHASES.find(
    phase => PHASE_CONFIG[phase].order === currentOrder + 1
  );
  
  return nextPhase || null;
}

// Função para obter a fase anterior
export function getPreviousPhase(currentPhase: ProjectPhase): ProjectPhase | null {
  const currentOrder = PHASE_CONFIG[currentPhase].order;
  const previousPhase = PROJECT_PHASES.find(
    phase => PHASE_CONFIG[phase].order === currentOrder - 1
  );
  
  return previousPhase || null;
}